import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { SarvaLogo } from "./SarvaMark";

const LINKS = [
  { to: "/services", label: "Services" },
  { to: "/solutions", label: "Solutions" },
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled || open ? "bg-ink/90 backdrop-blur-md border-b rule-dark" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="wrap flex items-center justify-between h-[76px]">
        <Link to="/" data-testid="nav-logo-link" aria-label="SarvaRitam home">
          <SarvaLogo />
        </Link>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-[13.5px] font-semibold tracking-wide transition-colors duration-200 ${
                  isActive ? "text-gold" : "text-mute hover:text-white"
                }`
              }
              data-testid={`nav-${l.label.toLowerCase()}-link`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <span className="hidden md:inline-flex">
            <Link to="/contact" className="btn-outline-gold" data-testid="nav-book-consultation-button">
              Book Consultation
            </Link>
          </span>
          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid={open ? "mobile-menu-close-button" : "mobile-menu-open-button"}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-ink border-b rule-dark"
            data-testid="mobile-menu"
            aria-label="Mobile"
          >
            <div className="wrap py-6 flex flex-col">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="py-4 text-white text-xl font-display font-semibold border-b border-white/5"
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </Link>
              ))}
              <Link to="/esg-readiness-assessment" className="py-4 text-gold text-xl font-display font-semibold border-b border-white/5" data-testid="mobile-nav-assessment">
                ESG Assessment
              </Link>
              <Link to="/contact" className="btn-gold w-full justify-center mt-7 py-4" data-testid="mobile-book-consultation-button">
                Book Consultation
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
